export type LgpiClassificationInput = {
  product: string;
  prestation: string;
  genericMarker: string;
  explicitFamily?: string;
  explicitRole?: string;
  explicitType?: string;
};

type ClassifiableRow = {
  product: string;
  rotationFamily: string;
  genericRole: string;
  productType: string;
};

const normalized = (value: string) => value
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("fr")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const emptyMarkers = new Set(["", "0", "-", "null", "neant", "aucun", "non"]);

function cleanMarker(value: string) {
  const trimmed = value.trim();
  return emptyMarkers.has(normalized(trimmed)) ? "" : trimmed;
}

export function classifyLgpiProduct(input: LgpiClassificationInput) {
  const prestation = normalized(input.prestation);
  const explicitType = normalized(input.explicitType ?? "");
  const explicitRole = normalized(input.explicitRole ?? "");
  const genericMarker = cleanMarker(input.genericMarker);
  const explicitFamily = cleanMarker(input.explicitFamily ?? "");
  const isPhn = prestation === "phn" || prestation.startsWith("phn ") || prestation.includes(" prestation phn");
  const isGeneric = Boolean(genericMarker) || explicitRole.includes("generique") || explicitType.includes("generique");

  const productType = isPhn
    ? "PHN / conseil"
    : isGeneric
      ? "Générique"
      : explicitType.includes("rembours")
        ? "Remboursé"
        : "Princeps";

  return {
    rotationFamily: isGeneric ? genericMarker || explicitFamily : explicitFamily,
    genericRole: isGeneric ? "Générique" : isPhn ? "PHN" : "Princeps",
    productType,
  };
}

export function attachPrincepsToGenericFamilies<Row extends ClassifiableRow>(rows: Row[]) {
  const families = [...new Set(rows
    .filter((row) => row.genericRole === "Générique" && row.rotationFamily)
    .map((row) => row.rotationFamily.trim())
    .filter(Boolean))];

  return rows.map((row) => {
    if (row.rotationFamily || row.genericRole !== "Princeps") return row;
    const product = normalized(row.product);
    const family = families.find((candidate) => {
      const value = normalized(candidate);
      return value.length >= 5 && (product === value || product.includes(value) || value.includes(product));
    });
    return family ? { ...row, rotationFamily: family } : row;
  });
}
